import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from '@legendapp/state/react';
import { ui$, closeModal, setSkipDeleteConfirmation } from '../store';
import { Dialog, Button } from '../design-system';
import './ConfirmDeleteModal.css';

const ConfirmDeleteModal = () => {
  const { t } = useTranslation('modals');
  const modalData = useSelector(() => ui$.modalData.get());
  const [dontAskAgain, setDontAskAgain] = useState(false);
  const [extraChecked, setExtraChecked] = useState(false);
  const confirmButtonRef = useRef(null);

  // Support custom header/message via modalData, with defaults for article delete
  const header = modalData?.header || t('confirm.deleteArticleTitle');
  const message = modalData?.message || t('confirm.deleteArticleMessage');
  const confirmText = modalData?.confirmText || t('confirm.deleteButton');
  const variant = modalData?.variant || 'danger'; // 'danger' | 'primary'
  const showDontAskAgain = modalData?.showDontAskAgain !== false; // default true
  const extraCheckbox = modalData?.extraCheckbox || null; // { label, checkedConfirmText, checkedVariant }

  // Derive button text and style based on extra checkbox state
  const effectiveVariant = (extraChecked && extraCheckbox?.checkedVariant) ? extraCheckbox.checkedVariant : variant;
  const effectiveConfirmText = (extraChecked && extraCheckbox?.checkedConfirmText) ? extraCheckbox.checkedConfirmText : confirmText;

  // Enter 只激活当前聚焦的按钮：打开时焦点在确认按钮，Tab 到 Cancel 后 Enter 必须是取消，
  // 所以这里不能挂 document 级 Enter 监听。

  const handleConfirm = useCallback(() => {
    if (showDontAskAgain && dontAskAgain) {
      setSkipDeleteConfirmation(true);
    }
    modalData?.onConfirm?.(extraChecked);
    closeModal();
  }, [showDontAskAgain, dontAskAgain, modalData, extraChecked]);

  const confirmVariant = effectiveVariant === 'primary' ? 'primary' : 'danger';

  return (
    <Dialog
      isOpen={true}
      onClose={closeModal}
      title={header}
      size="sm"
      centered
      initialFocusRef={confirmButtonRef}
      footer={
        <>
          <Button className="confirm-delete-action" variant="neutral" onClick={closeModal}>
            {t('button.cancel', 'Cancel')}
          </Button>
          <Button className="confirm-delete-action" ref={confirmButtonRef} variant={confirmVariant} onClick={handleConfirm}>
            {effectiveConfirmText}
          </Button>
        </>
      }
    >
      <p className="confirm-delete-message">{message}</p>

      {extraCheckbox && (
        <label className={`confirm-delete-checkbox confirm-delete-extra-checkbox${extraChecked ? ' confirm-delete-extra-checkbox--checked' : ''}`}>
          <input
            type="checkbox"
            checked={extraChecked}
            onChange={e => setExtraChecked(e.target.checked)}
          />
          <span>{extraCheckbox.label}</span>
        </label>
      )}

      {showDontAskAgain && (
        <label className="confirm-delete-checkbox">
          <input
            type="checkbox"
            checked={dontAskAgain}
            onChange={e => setDontAskAgain(e.target.checked)}
          />
          <span>{t('confirm.dontAskAgain')}</span>
        </label>
      )}
    </Dialog>
  );
};

export default ConfirmDeleteModal;
